/// <reference lib="dom" />
/// <reference path="./globals.d.ts" />
// ── Floor-Flow Prompts ───────────────────────────────────────

interface PromptPoseItem {
  id: string;
  label: string;
  desc: string;
}

interface PromptsConfig {
  PACKSHOT: string;
  MODEL: string;
  DESCRIPTION: string;
  SHOPIFY: string;
  SEO: string;
  JSON_DATA: string;
  MODEL_DETAIL: string;
  POSE_LIBRARY: PoseItem[];
  ENVIRONMENTS: EnvironmentItem[];
  LIGHTINGS: LightingItem[];
  CAMERAS: CameraItem[];
  FORMATS: FormatItem[];
  SEASONS: SeasonItem[];
  STUDIO_ANALYZE: string;
  STUDIO_GENERATE: string;
  STUDIO_SHOT: string;
}

const PROMPTS: PromptsConfig = {

PACKSHOT: `Make a product photo for a webshop of the garment in the images below: {{STYLE_NAME}}, seen from the {{VIEW}}.

THE IMAGES
{{IMAGES}}

THE SHOT
{{STYLE}}

THE GARMENT, EXACTLY AS IT IS
- It is this garment and no other: the same cut, length, proportions, fabric, print, graphics, logos, labels, buttons, zips, pockets, seams and stitching. Never add, remove, move, mirror, simplify or restyle a detail, and never add one because garments like it usually have it.
- {{COLOUR}}
- {{LABELS}}
- Freshly steamed and pressed: smooth all over, with none of the wrinkles, creases or folds from lying on the floor, while the fabric keeps its own weave or knit and looks like real cloth in soft light, never plastic or waxy.
- One whole garment: the sleeves attached where they belong, nothing doubled, detached or floating. Every edge (hem, cuffs, collar, front edges) is finished, sharp and clean, never frayed, torn or fading out.

THE BACKGROUND
Pure white, #FFFFFF, in every pixel outside the garment: under the hem, beside and between the sleeves, and in all four corners. The floor and everything else in the photos is gone. No surface, no floor line, no shadow under or behind the garment, no reflection, no grey, beige, cream or warm tint, no gradient or vignette. Shading belongs on the fabric only.{{CUTOUT}}

THE FRAME
{{FORMAT}}. The whole garment is inside the frame, centred, with an even white margin of about a tenth of the frame on every side; no sleeve, hem or collar touches an edge.

CHECK BEFORE YOU ANSWER
- Every pixel outside the garment, under the hem and in the corners, is #FFFFFF.
- Every letter on the labels and prints is as in the images; nothing is invented, and nothing is there that the images do not show.
- {{STYLE_CHECK}}
- The colour is the garment's own: not deeper, brighter, more saturated, cooler or warmer.
- The garment is smooth and pressed, without wrinkles or creases.

No text, borders or watermarks in the picture.`,

DESCRIPTION: `Describe the garment in the images, for a webshop's product text.{{PRODUCT}}

THE IMAGES
{{IMAGES}}

In plain English sentences, describe only what can be seen:
1. The garment type and category.
2. Its colour or colours.
3. The fabric and material: from the label if it can be read, otherwise as it looks.
4. The fit and cut.
5. Prints, logos, graphics and embroidery: what they are, where they sit, and any text on them, letter for letter.
6. The construction: collar, closure, sleeves, cuffs, hem, pockets, seams and stitching.
7. The brand and the care instructions, if a label shows them.
8. The style, and who it suits.
Never guess at what cannot be seen; say that it is not visible.`,

SHOPIFY: `Write the Shopify product text for this garment, in Danish.{{PRODUCT}}

Write as a good copywriter for a Danish fashion webshop: modern, warm and concrete, about how the garment fits, feels and is worn, not a list of claims. No clichés, no exaggeration, and nothing the description below does not support.

Return exactly this, and nothing else:

Titel:
[a short product title]

Kort beskrivelse:
[1-2 sentences on what the garment is and why it is good]

Lang beskrivelse:
[3-4 sentences on fit, feel and how to style it]

Egenskaber:
• [Materiale] - [a short detail]
• [Pasform] - [a short detail]
• [Detalje] - [a short detail]
• [Detalje] - [a short detail]

THE DESCRIPTION
{{DESC}}`,

SEO: `Write the meta description for this garment's product page, in Danish.{{PRODUCT}}

At most 150 characters, counted strictly. Natural and specific, with the words people search for (the garment type, colour, material and brand), and ending with a short call to action such as "Køb online her." Return only the meta description, nothing else.

THE DESCRIPTION
{{DESC}}`,

JSON_DATA: `Return the product data for this garment as valid JSON only: no explanation and no code fence.{{PRODUCT}}

{"title":"","short_description":"","long_description":"","features":["","",""],"meta_description":"","tags":["",""]}

All text in Danish. meta_description at most 160 characters. tags: short search words (the garment type, colour, material and brand).

THE DESCRIPTION
{{DESC}}`,

// ── Poses prompts ────────────────────────────────────────────

// Detail shots (phase 5): a close-up of the model shot, with the packshot as the
// truth for the garment. Fields, filled in by buildDetailShot (pipeline-phases.ts):
//   {{DETAIL}}    what the close-up shows (DETAIL_SHOTS)
//   {{CLOSEUPS}}  a sentence on the uploaded close-up photos, or nothing
MODEL: `Make a model photo for a webshop: a model wearing the garment, seen from the {{VIEW}}.

THE IMAGES
{{IMAGES}}

THE PERSON
{{PERSON}}

THE GARMENT, WORN
- It is exactly the garment in the packshot: the same cut, length, colour, fabric, print, graphics, logos, labels, buttons, zips, pockets, seams and stitching. Never add, remove, move, mirror, simplify or restyle a detail, and never add one because garments like it usually have it.
- Colour: exactly the packshot's colour, as that garment looks in this light: not deeper, brighter, more saturated, cooler or warmer.
- Labels and text: every letter, logo and print is copied from the packshot, letter for letter. Where text is too small or blurred to read, that spot is plain fabric: no letter is ever guessed. The label inside the neck does not show on a person.
- Worn, not pasted on: a real body fills it. The neck comes up through the collar, with skin and the shadow under the jaw between chin and collar; the collar stands around the neck; the shoulder seams sit on the shoulders; buttons sit on a raised placket; the sleeves wrap the arms.
- Smooth and freshly pressed, as on a webshop: the garment lies smooth over the body, with only the few soft folds a well-fitting garment has where the body bends, at the armpits and the waist. No wrinkles, creases, crumpling or bunching, and none of the creases from lying on a floor. The weave or knit shows.
- {{OUTFIT}}

THE PHOTO
Full body, as a webshop shows it: the whole person from just above the head to just below the shoes, standing upright in the middle of the frame. The person fills about 85% of the frame's height, with clear space above the head and below the shoes; in a square or wide frame that makes the person smaller, never cut off. The legs have their natural length. {{FORMAT}}.
{{BACKGROUND}}
A real photograph in soft, even studio light: skin with its pores, hair as strands, hands and feet whole and natural. No text, borders or watermarks.

CHECK BEFORE YOU ANSWER
- The garment matches the packshot in colour, print, letters and every detail, and it is smooth and pressed, without wrinkles.
- The whole person is in the frame: the top of the head and the shoes both show, with space above and below.
- {{BACKGROUND_CHECK}}`,

MODEL_DETAIL: `Make a detail photo for a webshop: a close-up of the garment the model wears in Image 1, showing {{DETAIL}}.

THE IMAGES
Image 1: the model photo the close-up belongs to: the same person, outfit, pose, light, background and colour, as if the photographer stepped in closer during the same shoot.
Image 2: the packshot of the garment: the truth for every detail.{{CLOSEUPS}}

THE GARMENT
- Exactly as in the packshot: its colour, fabric, texture, print, logos, labels, stitching, buttons and zips.
- Labels and text: copied letter for letter. Where text is too small or blurred to read, that spot is plain fabric: no letter is ever guessed.
- Worn, smooth and freshly pressed, with only the soft drape of a body: no wrinkles or creases. The weave or knit is visible.

THE PHOTO
Close, so the detail fills most of the picture and is sharp. The frame cuts the body naturally; the face is not in the picture, unless the detail is at the neck, and then only the chin. A real photograph with a sharp lens, natural depth of field and true colours. No text, borders or watermarks.`,

POSE_LIBRARY: [
    { id: 'p0', label: 'Front Stand',    desc: "Standing front-facing, relaxed with arms naturally at sides, slight smile, weight evenly distributed — classic e-commerce lookbook pose" },
    { id: 'p1', label: 'Hand on Hip',    desc: "Confident stance with one hand resting on hip, body angled slightly to camera, editorial fashion mood" },
    { id: 'p2', label: 'Walking',        desc: "Walking mid-stride, one leg forward, natural arm swing, dynamic movement captured — fashion runway energy" },
    { id: 'p3', label: '¾ Angle',        desc: "Three-quarter angle view, body turned 45 degrees to camera, looking toward camera, one hand relaxed — shows garment silhouette" },
    { id: 'p4', label: 'Full Back',      desc: "Full back view, model turned completely away from camera, head turned slightly to the side, showing the back of all garments" },
    { id: 'p5', label: 'Over Shoulder',  desc: "Looking over shoulder, body turned 3/4 away from camera, face visible in profile, rear garment details on display" },
    { id: 'p6', label: 'Leaning',        desc: "Casual leaning pose, weight shifted to one leg, one knee slightly bent, arms relaxed — natural street-style mood" },
    { id: 'p7', label: 'Arms Crossed',   desc: "Arms gently crossed at waist level, standing front-facing, composed and confident — editorial catalogue style" },
    { id: 'p8', label: 'Side Profile',   desc: "Side profile view, model turned 90 degrees, full silhouette visible, one foot slightly forward — shows garment shape and drape" },
    { id: 'p9', label: 'Fashion Walk',   desc: "Stepping forward toward camera, one foot ahead, slight forward lean, dynamic editorial fashion walk — energetic lookbook feel" },
],

// ── Campaign variables: Environment × Lighting × Camera ──────

ENVIRONMENTS: [
    { id: 'e0', label: 'Studio White',     desc: "Clean seamless white cyclorama studio background, minimal, editorial e-commerce setting, no distractions" },
    { id: 'e1', label: 'Concrete Wall',    desc: "Rough industrial concrete wall background, urban editorial mood, subtle texture, moody neutral grey tones" },
    { id: 'e2', label: 'Sunset Street',    desc: "European city street at golden hour sunset, warm orange light bouncing off buildings, cinematic urban fashion editorial" },
    { id: 'e3', label: 'Nordic Forest',    desc: "Misty Scandinavian pine forest, soft diffused daylight, atmospheric nature setting, cold tones, premium outdoor mood" },
    { id: 'e4', label: 'Luxury Interior',  desc: "Modern luxury interior — marble floor, minimalist designer furniture, soft warm ambient lighting, high-end editorial atmosphere" },
    { id: 'e6', label: 'Desert Dunes',     desc: "Vast sand desert dunes at golden hour, warm beige tones, minimal horizon, editorial travel fashion mood" },
    { id: 'e7', label: 'Rainy Asphalt',    desc: "Wet city asphalt after rain, reflections of neon signs, moody blue-teal atmosphere, cinematic noir fashion editorial" },
    { id: 'e8', label: 'Minimal Pastel',   desc: "Solid pastel color backdrop (soft pink or muted sage), clean minimalist fashion editorial, playful commercial mood" },
    { id: 'e9', label: 'Brutalist Plaza',  desc: "Brutalist concrete architecture plaza, hard geometric shapes, overcast diffused light, high-fashion architectural editorial" },
],

LIGHTINGS: [
    { id: 'l0', label: 'Soft Studio',   desc: "Soft even studio lighting, large diffused softbox, minimal shadows, clean commercial e-commerce look" },
    { id: 'l2', label: 'Hard Flash',    desc: "Direct on-camera flash, harsh sharp shadows, raw editorial 90s fashion mood, punchy contrast, deep blacks" },
    { id: 'l3', label: 'Moody Low Key', desc: "Dramatic low-key lighting, deep shadows, single directional light source, moody film-noir cinematic atmosphere" },
    { id: 'l4', label: 'Overcast Soft', desc: "Soft overcast daylight, wraparound shadows, even skin tones, natural cool diffused light — classic editorial mood" },
    { id: 'l7', label: 'Window Light',  desc: "Soft natural window light from the side, Vermeer-style painterly shadows, warm intimate editorial feel" },
],

CAMERAS: [
    { id: 'c0', label: 'Full Body',      desc: "Full body shot head to toe, subject centered, eye-level camera, 50mm lens feel, complete outfit visible" },
    { id: 'c1', label: 'Close Up',       desc: "Tight close-up framing from chest up, 85mm portrait lens compression, shallow depth of field, intimate editorial feel" },
    { id: 'c2', label: 'Low Angle',      desc: "Low angle camera shooting upward, hero stance, dramatic perspective, makes subject look powerful and tall, 24mm wide feel" },
    { id: 'c3', label: 'High Angle',     desc: "High angle camera looking down, playful editorial mood, subject looking up to lens, dynamic perspective" },
    { id: 'c5', label: 'Wide Environ.',  desc: "Wide establishing shot with lots of environment, subject smaller in frame, 24mm wide-angle, cinematic sense of place" },
    { id: 'c6', label: 'Detail Crop',    desc: "Asymmetric crop with subject off-center, architectural composition, editorial magazine layout feel" },
    { id: 'c7', label: 'Straight On',    desc: "Perfectly straight-on eye-level shot, symmetrical composition, classic commercial fashion framing" },
],

FORMATS: [
    { id: 'f3', label: '16:9',   desc: "Wide horizontal 16:9 cinematic widescreen aspect ratio. Landscape composition with cinematic horizontal framing, lots of horizontal space." },
    { id: 'f0', label: '1:1',    desc: "Square 1:1 aspect ratio composition. The image must be perfectly square — equal width and height. Compose the subject to work within a square frame." },
    { id: 'f4', label: '3:4',    desc: "Vertical 3:4 portrait aspect ratio. Classic portrait format, moderately taller than wide. Traditional portrait photography framing." },
    { id: 'f1', label: '4:5',    desc: "Vertical 4:5 portrait aspect ratio (like Instagram portrait). Slightly taller than wide. Compose the subject vertically with comfortable headroom and space below." },
    { id: 'f2', label: '9:16',   desc: "Tall vertical 9:16 aspect ratio (like Instagram Story or TikTok). Very tall and narrow portrait format. Full-length vertical composition." },
],

SEASONS: [
    { id: 's0', label: 'Spring',  desc: "Spring color grading — fresh soft pastel palette, cool-to-warm green undertones, gentle diffused daylight with a clean airy quality, light and uplifting editorial mood" },
    { id: 's1', label: 'Summer', desc: "Summer color grading — warm saturated golden tones, rich sunlit highlights, bright open contrast, sun-kissed warm skin tones, vibrant editorial summer energy" },
    { id: 's2', label: 'Autumn', desc: "Autumn color grading — warm amber, rust and burnt orange palette, soft directional low-angle light, deep earthy shadows, moody cinematic fall tones" },
    { id: 's3', label: 'Winter', desc: "Winter color grading — cool desaturated blue-grey palette, crisp high-contrast light, clean cold highlights, muted tonal range, minimal editorial winter mood" },
],

// ── Studio ───────────────────────────────────────────────
// Two prompts, both edited in the prompt manager. STUDIO_ANALYZE reads the
// chosen reference photos and the packshot and writes the notes for the edit;
// STUDIO_GENERATE edits the first reference photo: the same person, the other
// clothes, the pose and the place, with the packshot garment put on. The
// fields are listed in pipeline-campaign.ts.
STUDIO_ANALYZE: `You prepare a photo edit for {{BRAND}}'s {{SEASON}} campaign. The first {{REFERENCE_COUNT}} image(s) are photographs from the brand's own campaign. The first of them is the photograph that will be edited; any others are from the same shoot and only show more of it. The last {{GARMENT_COUNT}} image(s) are a product packshot of one garment ({{GARMENT_VIEWS}}).

An image model will dress the person in the first photograph in the packshot garment and change nothing else: the same person, the other clothes, the pose, the place and the light stay as they are. It sees the photograph and the packshot, and reads your notes. Write the notes it needs.

Describe only what is visible. Do not name, identify or guess who anyone is.

Under these headings, in plain sentences, as concretely as you can:

REPLACES: which piece of clothing in the first photograph the packshot garment takes the place of: a jacket or coat replaces the outer layer, a top the top, trousers the trousers, and so on. If there are several people, which one. If nothing of the same kind is worn, where the garment goes and what it is worn over or under.
HOW IT IS WORN: how the new garment sits on this person, following how they wear the piece it replaces: its length on this body, the fit, open or closed, the collar, how the cuffs and the hem meet the other clothes, tucked in or not, sleeves pushed up or not. What of the old piece must disappear, and what shows instead where the new garment covers less.
THE PERSON: what keeps them the same person: face shape and features, skin tone and texture, hair colour, length, parting and how it falls, facial hair, build, expression.
KEEP: everything else worn, item by item, with colour, material and fit: the other garments, shoes, socks, bags, jewellery, glasses, hats.
POSE: stance, weight, hands, head, gaze.
PHOTO: the place and what is behind the person; the light: where it comes from, how hard or soft, its colour, how shadows fall on the face, the clothes and the ground; the camera: focal length, height, distance, framing, depth of field; colour, contrast and grain. What lies just beyond the edges of the photograph, for when the frame must be wider or taller.
THE GARMENT: the packshot garment's details that are easy to get wrong: exact colour, material and texture, print, logos, labels, buttons, zips, pockets, seams, length.

At most 400 words. No marketing language and no vague adjectives.`,

STUDIO_GENERATE: `Edit a real photograph. The first image is a photograph from {{BRAND}}'s own {{SEASON}} campaign. The {{GARMENT_COUNT}} image(s) after it are a product packshot of one garment ({{GARMENT_VIEWS}}).

THE EDIT
Dress the person in the photograph in the packshot garment, in place of the piece of clothing it replaces. Change nothing else. The result is the same photograph, taken in the same second, with the new garment worn instead; only the framing is as FRAME below asks.

KEEP, EXACTLY AS IN THE PHOTOGRAPH
- The same person: the same face and features, expression, skin, hair, facial hair, body and build. Not a similar person: this person.
- Everything else they wear: every other garment, the shoes, bags, jewellery, glasses and other accessories, in their colours, fit and creases.
- The pose, the hands, the head and the gaze.
- The place and the background, the light and the shadows, the colour and the grain, and the camera's height, lens and depth of field, except where FRAME asks for a closer or wider picture.
- Anyone else in the photograph, as they are.

THE GARMENT
It is exactly the packshot garment: the same cut, length, colour, fabric, texture, print, graphics, logos, labels, buttons, zips, pockets, seams and proportions. Never invent, recolour, simplify, mirror or move a detail. Where a packshot shows the back, that is how the back looks. It fits this person in a size that suits them and is worn as real clothing is: it has weight, drapes, folds where the body bends, pulls at the shoulders and meets the other clothes as they would. Where it covers less than the piece it replaces, the person and their other clothes continue as they would be underneath.

The light of the photograph falls on it: from the same direction, with the same hardness, shadows, colour of light and contrast as on the rest of the photo. It has the same sharpness, noise and grain as everything around it and never looks pasted in.

NOTES FROM THE PHOTO DIRECTOR
{{REFERENCE_DESCRIPTION}}

REAL
It stays a real photograph from a camera: skin with its pores and marks, hair as strands, fabric with its weave and real wrinkles, hands with five fingers. No smoothing, no sharpening, no glow, no plastic or waxy surfaces, no melted details, no garbled letters in logos or labels.

FRAME
{{SHOT}}
{{FORMAT}}, {{WIDTH}} by {{HEIGHT}} pixels. Where the frame reaches beyond the photograph, continue the same place, and the same person and clothes, as the camera would have seen them. Never stretch or squash.

No text, borders or watermarks.`,

STUDIO_SHOT: `Image 1 is a crop from a photograph of {{BRAND}}'s {{SEASON}} campaign, enlarged, so it is soft. The {{GARMENT_COUNT}} image(s) after it are a product packshot of the garment the person wears ({{GARMENT_VIEWS}}).

Make Image 1 the sharp photograph the camera would have taken at this framing: {{SHOT}}.

KEEP, EXACTLY AS IN IMAGE 1
- The framing: what is in the picture and where it is, to every edge. Do not zoom out, do not add what lies beyond the edges, do not move or turn the person.
- The person: face and features, expression, skin, hair, facial hair and body; the pose and the hands.
- Everything else they wear, the place and the background, the light, the shadows and the colour.

THE GARMENT
It is exactly the packshot garment: the same cut, colour, fabric, texture, print, graphics, logos, labels, buttons, zips, pockets and seams. The print, the logo and every letter are copied from the packshot, sharp and readable wherever they show, at the size and place they have on the garment. Never invent, recolour, simplify, mirror or move a detail. It drapes and folds on the body as in Image 1.

REAL
A real photograph from a camera at this distance: skin with its pores and marks, hair as strands, fabric with its weave and stitching, hands with five fingers. No smoothing, no glow, no plastic or waxy surfaces, no garbled letters.

FRAME
{{FORMAT}}, {{WIDTH}} by {{HEIGHT}} pixels, the framing of Image 1. No text, borders or watermarks.`,

};
